<template>
  <div
      :class="[
      'd-flex flex-column gmb-section',
      { 'maxh-100': !maxHeight },
      {
        'p-1 rounded-1 gmb-section--selectable': isSelectable,
      },
      {
        'gmb-section--selected':
          selfCheckboxIsActivated || selfRadioButtonIsActivated,
      },
    ]"
      :id="selfDomNodeId"
      :style="{ maxHeight: maxHeight, height: finalHeight }"
  >
    <GmbRow
        :alignControlsOnTop="alignControlsOnTop"
        class="gmb-section__header-row"
        :collapseId="collapse.domId"
        :collapseIsVisible="collapse.isVisible"
        :controlValue="selfControlValue"
        :isCheckbox="isCheckbox"
        :isRadioButton="isRadioButton"
        isSelectableTextStyle
        ref="headerGmbRow"
        :switcherStyle="switcherStyle"
        @click.native="toggleCollapseVisibility"
        @checkbox-state-changed="
        (newCheckboxState) => onSelfCheckboxStateChanged(newCheckboxState)
      "
        @radio-button-state-changed="
        (newRadioButtonState) =>
          onSelfRadioButtonStateChanged(newRadioButtonState)
      "
    >
      <template #picto>
        <slot name="picto"/>
      </template>

      <FdText
          :bold="
          isDisplayLevel1 ||
          isDisplayLevel2 ||
          isDisplayLevel3 ||
          isDisplayLevel4
        "
          :colorTheme4="isDisplayLevel1"
          :colorWhite="isDisplayLevel2 || isDisplayLevel3 || isDisplayLevel4"
          :fontSizeBigger3="isDisplayLevel1"
          :fontSizeBigger2="isDisplayLevel2"
          :fontSizeBigger1="isDisplayLevel3"
          :fontSizeNormal="isDisplayLevel4"
          :tag="headingTag"
          :uppercase="isDisplayLevel1 || isDisplayLevel2"
      >{{ title }}
      </FdText
      >
      <FdText v-if="subtitle" colorLightGrey fontSizeSmaller2 tag="span">{{
          subtitle
        }}
      </FdText>

      <template #infoSticker>
        <slot name="infoSticker"/>
      </template>
    </GmbRow>

    <b-collapse
        v-if="hasDefaultContent"
        v-model="collapse.isVisible"
        :class="[
        { 'd-flex': collapse.isVisible },
        'flex-grow-1 mh-0 flex-column',
      ]"
        :id="collapse.id"
    >
      <div
          :class="[
          'd-flex flex-grow-1 mh-0 py-2 overflow-y-auto flex-column gmb-section__main-content-container',
          {
            'mt-1 border-top pt-1 gmb-section__main-content-container--no-divider':
              isSelectable,
          },
          {
            'border-left gmb-section__main-content-container--divider':
              !isSelectable,
          },
        ]"
      >
        <GmbCheckboxGroup
            v-if="childCheckboxActivatedValuesModel !== undefined"
            v-model="childCheckboxActivatedValuesModel"
            :allValues="childCheckboxAllValues"
            :disableSearch="disableSearch"
            :searchPlaceholder="searchPlaceholder"
            :disableSelectAllOrNoneButton="disableSelectAllOrNoneButton"
            :id="childControlGroupId"
            :keywordObjs="childControlKeywordObjs"
            :switcherStyle="switcherStyle"
        >
          <slot/>
        </GmbCheckboxGroup>

        <GmbRadioButtonGroup
            v-else-if="childRadioButtonActivatedValue != undefined"
            :activatedValue="childRadioButtonActivatedValue"
            :allValues="childRadioButtonAllValues"
            :disableSearch="disableSearch"
            :id="childControlGroupId"
            :keywordObjs="childControlKeywordObjs"
            @activated-value-changed="
            (newActivatedValue) =>
              $emit('child-radio-button-value-changed', newActivatedValue)
          "
        >
          <slot/>
        </GmbRadioButtonGroup>

        <div
            v-else
            class="d-flex flex-grow-1 mh-0 flex-column gmb-section__main-content-scroll-wrapper"
        >
          <FdScroll y>
            <div
                class="d-flex flex-grow-1 mh-0 flex-column gmb-section__main-content-wrapper"
            >
              <slot/>
            </div>
          </FdScroll>
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import {getUniqueId, hasEnrichedSlot} from "@/helpers.js";

import FdScroll from "@/flexdesign/Scroll";
import FdText from "@/flexdesign/Text";

import GmbCheckboxGroup from "@/widgets/CheckboxGroup";
import GmbRadioButtonGroup from "@/widgets/RadioButtonGroup";
import GmbRow from "@/widgets/Row";

export default {
  name: "GmbSection",

  components: {
    FdScroll,
    FdText,

    GmbCheckboxGroup,
    GmbRadioButtonGroup,
    GmbRow,
  },

  props: {
    alignControlsOnTop: Boolean,
    childCheckboxActivatedValues: Array,
    childCheckboxAllValues: Array,
    childControlGroupId: String,
    childControlKeywordObjs: Array,
    childRadioButtonActivatedValue: String,
    childRadioButtonAllValues: Array,
    collapseIsVisibleByDefault: Boolean,
    disableSearch: Boolean,
    disableSelectAllOrNoneButton: Boolean,
    displayLevel: Number,
    height: String,
    isCheckbox: Boolean,
    isCollapsible: Boolean,
    isRadioButton: Boolean,
    lockHeader: Boolean,
    maxHeight: String,
    searchPlaceholder: String,
    selfControlGroupId: String,
    selfControlValue: String,
    subtitle: String,
    switcherStyle: Boolean,
    title: String,
  },

  data() {
    return {
      collapse: {
        domId: undefined,
        isVisible: true,
      },
      headingTag: "span",
      isSelectable: false,
      selfCheckboxIsActivated: undefined,
      selfRadioButtonIsActivated: undefined,
    };
  },

  computed: {
    childCheckboxActivatedValuesModel: {
      get() {
        return this.childCheckboxActivatedValues;
      },
      set(newActivatedValues) {
        this.$emit(
            "child-checkbox-activated-values-changed",
            newActivatedValues
        );
      },
    },

    finalHeight() {
      return this.height === "parent" ? "100%" : this.height;
    },

    hasDefaultContent() {
      return hasEnrichedSlot(this, "default");
    },

    isDisplayLevel1() {
      return this.displayLevel === 1;
    },

    isDisplayLevel2() {
      return this.displayLevel === 2;
    },

    isDisplayLevel3() {
      return this.displayLevel === 3;
    },

    isDisplayLevel4() {
      return this.displayLevel === 4;
    },

    selfDomNodeId() {
      return this.selfControlGroupId
          ? this.selfControlGroupId + "-" + this.selfControlValue
          : this.selfControlValue;
    },
  },

  watch: {},

  beforeCreate() {
  },

  created() {
    if (this.hasDefaultContent && this.isCollapsible) {
      this.collapse.domId = "collapse-" + getUniqueId();
      if (!this.collapseIsVisibleByDefault) this.collapse.isVisible = false;
    }

    this.isSelectable = this.isCheckbox || this.isRadioButton;

    if (this.isDisplayLevel1) this.headingTag = "h3";
    if (this.isDisplayLevel2) this.headingTag = "h4";
    if (this.isDisplayLevel3) this.headingTag = "h5";
    if (this.isDisplayLevel4) this.headingTag = "h6";
  },

  beforeMount() {
  },

  mounted() {
    if (this.isCheckbox)
      this.selfCheckboxIsActivated =
          this.$refs.headerGmbRow.checkboxIsActivated;

    if (this.isRadioButton)
      this.selfRadioButtonIsActivated =
          this.$refs.headerGmbRow.radioButtonIsActivated;
  },

  beforeUpdate() {
  },

  updated() {
  },

  beforeUnmount() {
  },

  unmounted() {
  },

  methods: {
    onSelfCheckboxStateChanged(newCheckboxState) {
      this.selfCheckboxIsActivated = newCheckboxState;
      this.$emit("self-checkbox-state-changed", this.selfCheckboxIsActivated);
    },

    onSelfRadioButtonStateChanged(newCheckboxState) {
      this.selfRadioButtonIsActivated = newCheckboxState;
      this.$emit(
          "self-radio-button-state-changed",
          this.selfRadioButtonIsActivated
      );
    },

    toggleCollapseVisibility() {
      if (this.hasDefaultContent && this.isCollapsible)
        this.collapse.isVisible = !this.collapse.isVisible;
    },
  },
};
</script>

<style lang="scss">
@import "@/sass/settings/colors";
@import "@/sass/settings/dimensions";
@import "@/sass/bootstrap-overloads/colors";
@import "@/../node_modules/bootstrap/scss/functions";
@import "@/../node_modules/bootstrap/scss/mixins";
@import "@/../node_modules/bootstrap/scss/variables";
@import "@/../node_modules/bootstrap/scss/utilities/spacing";
@import "@/sass/settings/colors";

/* =========================================================================
 * SECTION
 * ========================================================================= */

.gmb-section {
  &--selectable {
    &:not(.gmb-section--selectable .gmb-section--selectable) {
      background-color: rgba($gmb-color-theme-2, 0.2);

      &.gmb-section--selected {
        background-color: rgba($gmb-color-theme-2, 0.6);
      }
    }
  }

  &__main-content-container {
    &--divider {
      margin-left: calc(#{$gmb-offset-3} / 2 + #{$spacer} * 0.5);
      padding-left: calc(#{$gmb-offset-3} + #{$spacer} * 0.5);
    }

    &--no-divider {
      padding-left: calc(#{$gmb-offset-3} + #{$spacer} * 0.25);
    }
  }

  &__main-content-scroll-wrapper {
    margin-left: calc(-1 * (#{$gmb-offset-3} / 2 + #{$spacer} * 0.5));
  }

  &__main-content-wrapper {
    & > *:not(.gmb-section:not(.gmb-section--selectable)) {
      margin-left: calc(#{$gmb-offset-3} / 2 + #{$spacer} * 0.5) !important;
    }
  }
}
</style>
